import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  BellIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { BellAlertIcon } from '@heroicons/react/24/solid'
import { toast } from 'react-hot-toast'
import { api } from '../utils/api'

interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  createdAt: string
  read: boolean
  data?: any
}

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const lastFetchTime = useRef<number>(0)

  // 防抖获取通知列表
  const fetchNotifications = useCallback(async (force = false) => {
    const now = Date.now()
    if (!force && now - lastFetchTime.current < 30000) {
      return
    }

    try {
      setLoading(true)
      lastFetchTime.current = now
      const response = await api.get('/notifications?limit=20')
      if (response.data?.success) {
        setNotifications(response.data.data.notifications)
      }
    } catch (error: any) {
      if (error.response?.status !== 429) {
        console.error('获取通知失败:', error)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // 防抖获取未读通知数量
  const fetchUnreadCount = useCallback(async (force = false) => {
    const now = Date.now()
    if (!force && now - lastFetchTime.current < 30000) {
      return
    }

    try {
      const response = await api.get('/notifications/unread-count')
      if (response.data?.success) {
        setUnreadCount(response.data.data.unreadCount)
      }
    } catch (error: any) {
      if (error.response?.status !== 429) {
        console.error('获取未读通知数量失败:', error)
      }
    }
  }, [])

  // 初始化加载
  useEffect(() => {
    fetchNotifications(true)
    fetchUnreadCount(true)
  }, [])

  // 当打开通知面板时刷新数据
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(true)
    }
  }, [isOpen, fetchNotifications])

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await api.patch(`/notifications/${notificationId}/read`)
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        )
        setUnreadCount((prev) => Math.max(0, prev - 1))
        toast.success('已标记为已读')
      }
    } catch (error) {
      console.error('标记为已读失败:', error)
      toast.error('标记为已读失败')
    }
  }

  const markAllAsRead = async () => {
    try {
      const response = await api.patch('/notifications/read-all')
      if (response.data.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
        setUnreadCount(0)
        toast.success('已全部标记为已读')
      }
    } catch (error) {
      console.error('全部标记为已读失败:', error)
      toast.error('全部标记为已读失败')
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`)
      if (response.data.success) {
        const notification = notifications.find((n) => n.id === notificationId)
        if (notification && !notification.read) {
          setUnreadCount((prev) => Math.max(0, prev - 1))
        }
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId))
        toast.success('通知已删除')
      }
    } catch (error) {
      console.error('删除通知失败:', error)
      toast.error('删除通知失败')
    }
  }

  const getNotificationConfig = (type: string) => {
    const configs = {
      success: {
        icon: CheckCircleIcon,
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        borderColor: 'border-l-emerald-500',
        gradient: 'from-emerald-50 to-teal-50'
      },
      warning: {
        icon: ExclamationTriangleIcon,
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        borderColor: 'border-l-amber-500',
        gradient: 'from-amber-50 to-yellow-50'
      },
      error: {
        icon: XCircleIcon,
        iconBg: 'bg-rose-100',
        iconColor: 'text-rose-600',
        borderColor: 'border-l-rose-500',
        gradient: 'from-rose-50 to-pink-50'
      },
      info: {
        icon: InformationCircleIcon,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        borderColor: 'border-l-blue-500',
        gradient: 'from-blue-50 to-indigo-50'
      }
    }
    return configs[type as keyof typeof configs] || configs.info
  }

  const formatTime = (timestamp: string) => {
    const now = new Date()
    const date = new Date(timestamp)
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 1) return '刚刚'
    if (minutes < 60) return `${minutes}分钟前`
    if (hours < 24) return `${hours}小时前`
    if (days < 7) return `${days}天前`
    return date.toLocaleDateString('zh-CN')
  }

  return (
    <div className="relative">
      {/* 通知按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
      >
        {unreadCount > 0 ? (
          <BellAlertIcon className="h-6 w-6 text-indigo-600 animate-pulse" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 shadow-lg shadow-rose-200/50 animate-in zoom-in duration-200">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 通知面板 */}
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* 面板 */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200">
            {/* 头部 */}
            <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <SparklesIcon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">通知中心</h3>
                    <p className="text-xs text-slate-500">
                      {unreadCount > 0 ? `${unreadCount} 条未读消息` : '暂无新消息'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors"
                    >
                      全部已读
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* 通知列表 */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BellIcon className="w-8 h-8 text-slate-400" />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-800">暂无通知</h4>
                  <p className="text-xs text-slate-500 mt-1">有新消息时会在这里显示</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {notifications.map((notification) => {
                    const config = getNotificationConfig(notification.type)
                    const Icon = config.icon

                    return (
                      <div
                        key={notification.id}
                        className={`relative p-4 transition-all hover:bg-slate-50 group ${
                          !notification.read ? `bg-gradient-to-r ${config.gradient}` : ''
                        }`}
                      >
                        {/* 未读指示条 */}
                        {!notification.read && (
                          <div
                            className={`absolute left-0 top-0 bottom-0 w-1 ${config.borderColor.replace(
                              'border-l-',
                              'bg-'
                            )}`}
                          />
                        )}

                        <div className="flex items-start gap-3">
                          {/* 图标 */}
                          <div
                            className={`flex-shrink-0 w-10 h-10 ${config.iconBg} rounded-xl flex items-center justify-center`}
                          >
                            <Icon className={`w-5 h-5 ${config.iconColor}`} />
                          </div>

                          {/* 内容 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={`text-sm font-semibold ${
                                  !notification.read ? 'text-slate-900' : 'text-slate-700'
                                }`}
                              >
                                {notification.title}
                              </p>
                              <span className="text-xs text-slate-400 whitespace-nowrap">
                                {formatTime(notification.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>

                            {/* 操作按钮 */}
                            <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!notification.read && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                  <CheckIcon className="w-3.5 h-3.5" />
                                  标记已读
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600 font-medium"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                                删除
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* 底部 */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => {
                    toast('查看所有通知功能开发中...')
                  }}
                  className="w-full text-center text-sm font-semibold text-indigo-600 hover:text-indigo-700 py-2 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  查看所有通知
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default NotificationCenter
